import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface MetadataCoverage {
  total: number;
  extracted: number;
  extractedPercent: string;
  withEmbedding: number;
  embeddingPercent: string;
}

export function useMetadataCoverage() {
  const query = useQuery({
    queryKey: ["metadata-coverage"],
    queryFn: async (): Promise<MetadataCoverage> => {
      const [totalRes, extractedRes, embeddingRes] = await Promise.all([
        supabase.from("memories").select("*", { count: "exact", head: true }),
        supabase
          .from("memories")
          .select("*", { count: "exact", head: true })
          .gte("extraction_version", 1),
        supabase
          .from("memories")
          .select("*", { count: "exact", head: true })
          .not("embedding", "is", null),
      ]);
      if (totalRes.error) throw totalRes.error;
      if (extractedRes.error) throw extractedRes.error;
      if (embeddingRes.error) throw embeddingRes.error;

      const total = totalRes.count ?? 0;
      const extracted = extractedRes.count ?? 0;
      const withEmbedding = embeddingRes.count ?? 0;
      const pct = (n: number) =>
        total === 0 ? "—" : `${Math.round((n / total) * 100)}%`;

      return {
        total,
        extracted,
        extractedPercent: pct(extracted),
        withEmbedding,
        embeddingPercent: pct(withEmbedding),
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
